import React, { useState, useEffect, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";
import useTranslation from "../../../../shared/hooks/useTranslation";
import serviceProviderSettingsApi from "../../../serviceBoard/api/serviceProviderSettingsApi";
import RfsOwnerMarker from "../../../servicing/components/rfsDetail/RfsOwnerMarker";
import OfferMapModal, { OfferMapData, ViewerLocation } from "./OfferMapModal";

export interface OfferMiniMapProps {
  /** The counterpart's location data */
  counterpartLat?: number;
  counterpartLng?: number;
  counterpartName: string;
  counterpartLogoUrl?: string;
  counterpartCity?: string;
  /** Pass false to fetch viewer's location from provider settings, true to use counterpart as the map-centered party */
  isOwner: boolean;
  offerNumber: string;
}

const mapStyles = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

const OfferMiniMap: React.FC<OfferMiniMapProps> = ({
  counterpartLat,
  counterpartLng,
  counterpartName,
  counterpartLogoUrl,
  counterpartCity,
  isOwner,
  offerNumber,
}) => {
  const { translate } = useTranslation(["serviceOffers", "serviceBoard"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [viewerLocation, setViewerLocation] = useState<ViewerLocation | null>(null);

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Fetch viewer's location (provider settings) on mount
  useEffect(() => {
    serviceProviderSettingsApi
      .getSettingsAsync()
      .then((settings) => {
        if (settings?.effectiveLatitude != null && settings?.effectiveLongitude != null) {
          setViewerLocation({
            lat: settings.effectiveLatitude,
            lng: settings.effectiveLongitude,
          });
        }
      })
      .catch(() => {
        // Silently fail
      });
  }, []);

  // Hide on phone
  if (isPhone) return null;

  const hasCoords =
    counterpartLat != null &&
    counterpartLng != null &&
    !isNaN(counterpartLat) &&
    !isNaN(counterpartLng);

  const mapHeight = isTablet ? 140 : 180;

  if (!hasCoords) {
    return (
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          backgroundColor: dt.inputBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <LocationOffOutlinedIcon sx={{ fontSize: 28, color: dt.textMuted, opacity: 0.5 }} />
        <Box
          sx={{
            fontSize: "12px",
            fontFamily: dt.font,
            fontWeight: 500,
            color: dt.textMuted,
          }}
        >
          {translate("serviceBoard:mapNoData")}
        </Box>
      </Box>
    );
  }

  const center = { lat: counterpartLat!, lng: counterpartLng! };

  const mapData: OfferMapData = {
    counterpartLat: counterpartLat!,
    counterpartLng: counterpartLng!,
    counterpartName,
    counterpartLogoUrl,
    counterpartCity,
    offerNumber,
  };

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          backgroundColor: dt.inputBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textMuted }}>
          {translate("serviceBoard:mapLoading")}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          cursor: "pointer",
        }}
        onClick={() => setModalOpen(true)}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={13}
          options={{
            styles: mapStyles,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "cooperative",
          }}
        >
          <RfsOwnerMarker
            position={center}
            companyName={counterpartName}
            logoUrl={counterpartLogoUrl}
            priorityCode={3}
            size={32}
          />
        </GoogleMap>
      </Box>

      <OfferMapModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mapData={mapData}
        viewerLocation={viewerLocation}
      />
    </>
  );
};

export default OfferMiniMap;
